import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { AFFILIATE, IMG } from "../lib/api";
import AffiliateButton from "../components/AffiliateButton";
import EmailCapture from "../components/EmailCapture";
import WaveDivider from "../components/WaveDivider";
import { ArrowRight, MapPin, Sparkles, TramFront, Utensils, Trees } from "lucide-react";
import { trackCTAClick } from "../lib/analytics";

const Home = () => {
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 600], [0, 120]);
  const heroScale = useTransform(scrollY, [0, 600], [1, 1.1]);
  const heroOpacity = useTransform(scrollY, [0, 500], [1, 0.3]);

  useEffect(() => {
    document.title = "Visit Bedford — Plan Your Trip to Universal Studios Bedford";
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative h-[100svh] min-h-[640px] w-full overflow-hidden" data-testid="home-hero">
        <motion.div
          style={{ y: heroY, scale: heroScale }}
          className="absolute inset-0 will-change-transform"
        >
          <img
            src={IMG.heroEmbankment}
            alt="Bedford Embankment on the River Great Ouse"
            className="w-full h-full object-cover"
          />
        </motion.div>
        <div className="absolute inset-0 hero-overlay" />

        <motion.div
          style={{ opacity: heroOpacity }}
          className="absolute inset-0 flex flex-col justify-end"
        >
          <div className="container-site pb-16 md:pb-24">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: "easeOut" }}
              className="max-w-4xl"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 backdrop-blur-md border border-white/25 text-white text-xs font-semibold tracking-[0.2em] uppercase mb-6">
                <Sparkles className="w-3.5 h-3.5" />
                Universal Studios Bedford — Opening 2031
              </div>
              <h1 className="font-serif-display text-5xl md:text-7xl lg:text-[5.5rem] font-black text-white leading-[0.95] text-balance mb-6">
                Planning a trip to <span className="italic text-[#FFB800]">Universal Studios Bedford</span>?
              </h1>
              <p className="text-white/90 text-lg md:text-2xl max-w-2xl font-light leading-relaxed mb-10">
                Find the best hotels, plan your trip, and discover Bedford — the riverside town behind the UK's biggest theme park.
              </p>
              <div className="flex flex-wrap gap-3 md:gap-4" data-testid="hero-cta-cluster">
                <AffiliateButton href={AFFILIATE.booking} variant="primary" testId="hero-find-hotels-cta">
                  Find Hotels
                </AffiliateButton>
                <Link
                  to="/plan-your-trip"
                  data-testid="hero-plan-trip-cta"
                  onClick={() => trackCTAClick({ label: "Plan Your Trip", location: "home-hero", destination: "/plan-your-trip" })}
                  className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-semibold text-sm md:text-base bg-white text-[#0B2C5B] hover:bg-[#FFF7E8] transition-all duration-300 shadow-md hover:-translate-y-0.5"
                >
                  Plan Your Trip <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/things-to-do"
                  data-testid="hero-explore-bedford-cta"
                  onClick={() => trackCTAClick({ label: "Explore Bedford", location: "home-hero", destination: "/things-to-do" })}
                  className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-semibold text-sm md:text-base bg-white/10 backdrop-blur-md border-2 border-white/40 text-white hover:bg-white/20 transition-all duration-300"
                >
                  Explore Bedford
                </Link>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Scroll hint */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-xs tracking-[0.3em] uppercase hidden md:flex flex-col items-center gap-2">
          <span>Scroll</span>
          <div className="w-px h-10 bg-gradient-to-b from-white/80 to-transparent" />
        </div>
      </section>

      {/* INTRO */}
      <section className="bg-[#FAFAFA] py-20 md:py-32">
        <div className="container-site grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-start">
          <div className="md:col-span-4">
            <div className="text-xs font-semibold tracking-[0.25em] uppercase text-[#2AB7E8] mb-4">
              Your independent Bedford guide
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl font-bold leading-tight text-[#0B2C5B]">
              The riverside town<br />behind the UK's biggest park.
            </h2>
          </div>
          <div className="md:col-span-7 md:col-start-6 space-y-6 text-lg leading-relaxed text-slate-700">
            <p>
              Bedford is about to become the UK's most talked-about destination. With Universal Studios opening on its doorstep, this quiet Georgian town on the River Great Ouse is quickly gaining the spotlight.
            </p>
            <p>
              We've built Visit Bedford to help you plan the perfect trip — from the best hotels near the park, to riverside walks, historic pubs and family-friendly day trips. No fluff. Just honest, local advice.
            </p>
            <div className="flex flex-wrap gap-3 pt-4">
              <Link
                to="/hotels"
                data-testid="intro-hotels-link"
                className="inline-flex items-center gap-2 text-[#0B2C5B] font-semibold border-b-2 border-[#FF8C00] pb-1 hover:text-[#FF8C00] transition"
              >
                Browse curated hotels <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Bento Grid: Popular Attractions */}
      <section className="bg-[#FAFAFA] pb-20 md:pb-32">
        <div className="container-site">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6">
            {/* Big card */}
            <Link to="/plan-your-trip" className="md:col-span-7 relative group rounded-3xl overflow-hidden aspect-[4/3] md:aspect-auto md:h-[520px]" data-testid="bento-theme-park">
              <img src={IMG.familyThemePark} alt="Family at theme park" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B2C5B]/90 via-[#0B2C5B]/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8 md:p-10 text-white">
                <div className="text-xs font-semibold tracking-[0.25em] uppercase text-[#FFB800] mb-3">01 — The Park</div>
                <h3 className="font-serif-display text-3xl md:text-4xl font-bold mb-3">Universal Studios Bedford</h3>
                <p className="text-white/80 max-w-lg leading-relaxed mb-4">
                  The UK's first Universal theme park is coming — and it's going to be huge. Plan your visit before everyone else does.
                </p>
                <span className="inline-flex items-center gap-2 font-semibold text-[#FFB800]">
                  Plan your visit <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </span>
              </div>
            </Link>

            <div className="md:col-span-5 grid grid-cols-1 gap-5 md:gap-6">
              <Link to="/things-to-do" className="relative group rounded-3xl overflow-hidden aspect-[4/3] md:aspect-auto md:h-[248px]" data-testid="bento-river">
                <img src={IMG.riverPark} alt="River Great Ouse" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#071F42]/80 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <div className="text-[10px] font-semibold tracking-[0.25em] uppercase text-[#9FD8F0] mb-2">02 — The River</div>
                  <h3 className="font-serif-display text-2xl font-bold">The Great Ouse &amp; Embankment</h3>
                </div>
              </Link>
              <Link to="/hotels" className="relative group rounded-3xl overflow-hidden aspect-[4/3] md:aspect-auto md:h-[248px]" data-testid="bento-hotels">
                <img src={IMG.luxuryHotel1} alt="Boutique hotel" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#071F42]/80 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <div className="text-[10px] font-semibold tracking-[0.25em] uppercase text-[#FFB800] mb-2">03 — Where To Stay</div>
                  <h3 className="font-serif-display text-2xl font-bold">Curated Hotels Near the Park</h3>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Quick stats ribbon */}
      <section className="bg-[#0B2C5B] text-white">
        <div className="container-site py-14 md:py-16 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { icon: TramFront, value: "40 min", label: "London to Bedford by train" },
            { icon: MapPin, value: "2031", label: "Universal Studios opening" },
            { icon: Utensils, value: "200+", label: "Local pubs & restaurants" },
            { icon: Trees, value: "300 acres", label: "Of riverside parks" },
          ].map((s, i) => (
            <div key={i} className="text-center md:text-left">
              <s.icon className="w-7 h-7 text-[#FFB800] mx-auto md:mx-0 mb-3" />
              <div className="font-serif-display text-3xl md:text-4xl font-bold">{s.value}</div>
              <div className="text-sm text-white/60 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      <WaveDivider variant="bottom" fill="#FAFAFA" bg="#0B2C5B" />

      {/* Email capture */}
      <EmailCapture source="homepage" />

      {/* Closing CTA */}
      <section className="relative bg-[#FAFAFA] py-20 md:py-28">
        <div className="container-site text-center max-w-3xl mx-auto">
          <h2 className="font-serif-display text-4xl md:text-5xl font-bold text-[#0B2C5B] mb-5 leading-tight">
            Ready to book your trip to Bedford?
          </h2>
          <p className="text-lg text-slate-700 mb-8">
            Compare hotels near Universal Studios and lock in early-bird rates before the crowds arrive.
          </p>
          <AffiliateButton href={AFFILIATE.booking} variant="primary" testId="home-final-cta">
            Compare Hotels on Booking.com
          </AffiliateButton>
        </div>
      </section>
    </div>
  );
};

export default Home;
